/**
 * styles.css — Goosecross Cellars tenant-scoped CSS.
 *
 * Injected AFTER src/styles/variables.css by init.js injectSharedStyles(), so these
 * :host(.ws-tenant-goosecross) rules override the base :host vars and the shared rich-card
 * scaffold. Moved verbatim out of variables.css (split per-winery for maintainability).
 */

/* ────────────────────────────────────────────────────────────────────
   Goosecross Cellars (`goosecross`)
   ────────────────────────────────────────────────────────────────────
   Visual cues from old script.js + styles.css on the `goosecross` branch:
   - Primary CTA is GOLD (#E8BF5C → #8B6B33 gradient on hover)
   - Card accents lean BURGUNDY (#7B0323) — most visible on membership cards
   - Body text TAN (#574D39) on a warm parchment-ish bg (#F5F1E9)
   The primary/background colors come from the admin theme (stub today); only
   non-admin-driven values live in this scope. */
:host(.ws-tenant-goosecross) {
  --ws-card-accent: #e8bf5c;
  --ws-card-secondary-accent: #8b6b33;
  --ws-drawer-cta: #e8bf5c;
  --ws-drawer-cta-text: #000;
  --ws-drawer-surface: #fef8e7;
  --ws-card-title-font: "Open Sans", sans-serif;
  --ws-mic-color: #4b412d;
  --ws-mic-bg: #d4d4d4;
  --ws-scrollbar-icon-color: #574d39;
  --ws-header-action-color: #574d39;
  --ws-header-logo-color: #000000;
}

:host(.ws-tenant-goosecross) .ws-avatar {
  height: 27px;
  width: 28px;
}

:host(.ws-tenant-goosecross) .ws-rich-review-button {
  border-radius: 1px;
}

:host(.ws-tenant-goosecross) #ws-launcher {
  right: -2px;
}
:host(.ws-tenant-goosecross) .launcher {
  color: #000000;
  width: 42px;
  height: 160px;

  gap: 4px;
  padding: 0;
  justify-content: center;
  box-shadow: none;
}
:host(.ws-tenant-goosecross) .launcher:hover {
  box-shadow: none;
}
:host(.ws-tenant-goosecross) .launcher:focus {
  outline: 0;
}
:host(.ws-tenant-goosecross) .launcher .label {
  font-size: 18px;
  font-weight: 400;
  letter-spacing: 0.36px;
  line-height: 22px;
}

:host(.ws-tenant-goosecross) .launcher > svg > path:not([mask]) {
  fill: #000000;
}

/* The live goose header is CREAM (the parchment background_color) with a dark
   "Goosecross" wordmark + dark controls — NOT the gold --ws-primary base header,
   and NOT white. The footer/input area is white instead (the inverse — see
   below). The launcher stays gold (primary), so the header can't follow primary.
   Logo/control colours are dark via header_logo_color (#000) +
   header_action_color (#574D39) in the tenant config. */
:host(.ws-tenant-goosecross) .ws-header {
  background: var(--ws-background);
  color: #574d39;
}
/* Footer + input area are WHITE on goose — the inverse of the cream header (live
   swaps these vs the base, where they default to var(--ws-background) = cream).
   BOTH the .ws-footer strip and the .ws-input-area composer must be white so the
   bottom region reads as one uniform band (not a two-tone cream/white split). */
:host(.ws-tenant-goosecross) .ws-footer,
:host(.ws-tenant-goosecross) .ws-input-area {
  background: #ffffff;
}
/* Goose shows the config Banner (banner_image_url image + banner_text/CTA strip)
   from the admin panel — NOT the old local 12-day rotating PromoPoster. Hide the
   promo poster so the banner isn't double-rendered with a local day-*.png. */
/* Higher specificity (.ws-promo-poster.ws-goose-promo) so this beats the
   .ws-goose-promo-fallback { display:flex } rule below — otherwise the legacy
   "A Weekend of Wine" fallback panel re-appears above the config banner. */
:host(.ws-tenant-goosecross) .ws-promo-poster.ws-goose-promo {
  display: none;
}
/* Goose wordmark is larger than the base 32px logo — the live header shows a
   bigger GOOSECROSS mark. (Estimated to match live; tune the height if needed.) */
:host(.ws-tenant-goosecross) .ws-header-logo img {
  height: 48px;
  max-width: 200px;
}
/* Banner image shows FULL (live shows the whole event poster). The base
   .ws-banner-img crops to a 110px object-fit:cover strip — let goose size to the
   image's natural height at full width instead. */
:host(.ws-tenant-goosecross) .ws-banner-img {
  height: auto;
  object-fit: contain;
}

/* main_banner_text overlay — old .chatbot-banner-text (right-aligned, 20px). */
:host(.ws-tenant-goosecross) .ws-banner-main-text {
  top: 50%;
  right: 10px;
  left: auto;
  transform: translateY(-50%);
  align-items: flex-end;
  gap: 4px;
}
:host(.ws-tenant-goosecross) .ws-banner-main-text span {
  font-family: "Open Sans", sans-serif;
  font-size: 20px;
  letter-spacing: 0.04em;
  text-align: right;
  white-space: nowrap;
}

/* Banner BOOK NOW — old .promo-text-right (ss1): white pill, brown text,
   rounded corners on the olive promo strip. Not the Fogarty transparent
   white-text CTA (ss2). */
:host(.ws-tenant-goosecross) .ws-banner-promo {
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-inline: 8px;
}

/* Undimmed promo bg (banner_promo_dim: false) is a single url() — override
   base .ws-banner-promo background-size (14px, cover) so the image covers
   normally instead of tiling at 14px. Same pattern as Wine Engine. */
:host(.ws-tenant-goosecross) .ws-banner-promo--image {
  background-color: transparent;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

:host(.ws-tenant-goosecross) .ws-banner-cta {
  background: #ffffff;
  color: #574d39;
  border: none;
  border-radius: 6px;
  padding: 10px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.2;
  text-transform: uppercase;
  text-align: center;
  max-width: 30%;
  flex-shrink: 0;
  white-space: nowrap;
}

:host(.ws-tenant-goosecross) .ws-banner-cta:hover {
  background: #ffffff;
  color: #574d39;
  opacity: 0.92;
}

:host(.ws-tenant-goosecross) .ws-bot-bubble {
  color: #000;
}

/* Original Goose .bot-message .message-content welcome background. */
:host(.ws-tenant-goosecross) .ws-welcome {
  background: linear-gradient(#f7f6f6, #ededed);
  border: none;
  color: #4d4d4d;
}

/* Old Goose addLoadingAnimationAsBotResponse: bare dark-gray .loader. */
:host(.ws-tenant-goosecross) .ws-typing {
  background: none;
  border: none;
  padding: 0;
}

:host(.ws-tenant-goosecross) .ws-typing-dots {
  background: radial-gradient(circle closest-side, #424245 90%, transparent)
    0 / 33.3333333333% 100% space;
}

/* Promo strip caption — Goose only: 16px bold. */
:host(.ws-tenant-goosecross) .ws-banner-text {
  flex: 1;
  min-width: 0;
  max-width: none;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
}

:host(.ws-tenant-goosecross) .ws-banner-text span ~ span {
  font-size: 13px;
  font-weight: 400;
  text-transform: none;
}

/* Reserve space for the decorative curve below the price line. */
:host(.ws-tenant-goosecross) .ws-banner-promo:has(.ws-banner-curve) {
  padding-bottom: 14px;
}
/* Old Goose #user-input + #voice_button form one outlined 38px field. */
:host(.ws-tenant-goosecross) .ws-input-wrap {
  border: 1px solid #405c6f;
  background: rgba(192, 195, 196, 0.1803921569);
  box-sizing: border-box;
  min-width: 0;
  height: 38px;
  padding: 0 4px 0 15px;
}

:host(.ws-tenant-goosecross) .ws-input {
  min-width: 0;
  padding: 8px 0;
  color: #797264;
  font-weight: 600;
  line-height: 20px;
}

:host(.ws-tenant-goosecross) .ws-input::placeholder {
  color: #797264;
  opacity: 1;
}

:host(.ws-tenant-goosecross) .ws-btn-mic {
  width: 26px;
  height: 26px;
}

:host(.ws-tenant-goosecross) .ws-btn-send {
  color: #574d39;
}

/* Welcome prompt chips — replicate the LIVE goose `.option-button` (verified via
   computed styles): white pill, brown (#574D39) text + border, SQUARE corners,
   sized to text (left-aligned, 8px indent), brown bolt icon, NO hover colour
   change. The widget renders `.ws-prompt-chip` (the old `.ws-prompt-button`
   class is no longer emitted). */
/* Chip layout (indent + shrink-to-text) is shared in ChatPanel.css .ws-prompts. */
:host(.ws-tenant-goosecross) .ws-prompts {
  gap: 5px;
}

/* Follow-up recommendation chips: ONE shared width, not shrink-to-text. Live
   goose renders these as `.option-button.same-opt-btn` inside
   `#button-container` — `max-width:90%` on the container + `width:100%` on each
   button, so all three boxes match. Reproduced here as max-width on the list
   (border-box, so the avatar indent is included, like the old 40px margin) +
   stretch so every chip fills it. Welcome/guided chips are untouched. */
:host(.ws-tenant-goosecross) .ws-prompts--recommendations {
  max-width: 90%;
  align-items: stretch;
}

:host(.ws-tenant-goosecross) .ws-prompt-chip {
  background: #ffffff;
  color: #574d39;
  border: 1px solid #574d39;
  border-radius: 0;
  padding: 8px 12px;
  font-size: 15px;
  font-weight: 600;
  gap: 5px;
  line-height: 16px;
  min-height: 35px;
}
:host(.ws-tenant-goosecross) .ws-prompt-chip:hover {
  background: #ffffff;
  color: #574d39;
  border-color: #574d39;
}
:host(.ws-tenant-goosecross) .ws-prompt-chip .ws-prompt-icon {
  color: inherit;
}
/* Selected (clicked) state — gold fill, matching the live changeBKG:
   #F8D47C bg, #8D6E35 border, black text/icon. */
:host(.ws-tenant-goosecross) .ws-prompt-chip.is-selected {
  background: #f8d47c !important;
  border-color: #8d6e35 !important;
  color: #000000 !important;
  font-weight: 600;
}
:host(.ws-tenant-goosecross) .ws-prompt-chip.is-selected .ws-prompt-icon {
  color: inherit;
}

/* Feature-card CTAs (Book Now / Join Now / Reserve) are GOLD for goose, not
   the burgundy --ws-card-accent the base CSS uses. Matches the old goose
   gold buttons. Covers the base TastingCard (goose uses it as-is) plus the
   goose Event/Membership overrides. The WineCard CTA has its own rule above. */
:host(.ws-tenant-goosecross) .ws-card-cta {
  background: #e8bf5c;
  color: #1a1a1a;
}
:host(.ws-tenant-goosecross) .ws-card-cta:hover {
  background: #f8d47c;
  opacity: 1;
}

/* Tasting-experience title and price share the wine-listing copper color. */
:host(.ws-tenant-goosecross) .ws-tasting-card .ws-card-cta,
:host(.ws-tenant-goosecross) .ws-tasting-card .ws-card-cta:hover {
  background: #f8d47c;
  border-color: #f8d47c;
  color: #000;
}

:host(.ws-tenant-goosecross) .ws-tasting-card .ws-card-title,
:host(.ws-tenant-goosecross) .ws-tasting-card .ws-card-price {
  color: var(--ws-card-secondary-accent);
}

/* Old createWineTastingCard SVG uses a burgundy stroke, distinct from text. */
:host(.ws-tenant-goosecross) .ws-tasting-card .ws-learnmore-icon {
  color: #6e1811;
}

/* Wine listing card — old createWineListing parity.
   Image at top, outlined pill "Tasting notes" button, bold title, then a
   horizontal row with price+size on the left and a content-sized BUY NOW
   button on the right. No appellation/varietal/alcohol meta lines —
   matches the old card. */
:host(.ws-tenant-goosecross) .ws-goose-winecard {
  width: 240px;
  flex: 0 0 auto;
  background: #ffffff;
  /* border: 1px solid #E5E0D7; */
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: var(--ws-shadow);
}
:host(.ws-tenant-goosecross) .ws-goose-head {
  position: relative;
  padding: 12px 12px 0;
  margin-bottom: 20px;
}
:host(.ws-tenant-goosecross) .ws-goose-image-area {
  position: relative;
  height: 170px;
}

:host(.ws-tenant-goosecross) .ws-goose-head .ws-card-img {
  width: 100%;
  height: 170px;
  object-fit: contain;
  display: block;
}
:host(.ws-tenant-goosecross) .ws-goose-soldout {
  position: absolute;
  left: 0;
  bottom: 8px;
  background: #7b0323;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  padding: 4px 10px;
}

/* "Tasting notes" outlined pill (old .tasting-notes-link). */
:host(.ws-tenant-goosecross) .ws-goose-tasting {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: 20px auto 10px;
  margin-bottom: 10px;
  border: 1px solid #f8d47c;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-secondary-accent, #8b6b33);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
}
:host(.ws-tenant-goosecross) .ws-goose-tasting:hover {
  border-color: #f8d47c;
  color: var(--ws-card-secondary-accent);
}

:host(.ws-tenant-goosecross) .ws-goose-winetitle {
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-secondary-accent);
  margin: 0;
  line-height: 1.25;
  /* max-height: 35px; */
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

:host(.ws-tenant-goosecross) .ws-goose-winecard-meta,
:host(.ws-tenant-goosecross) .ws-goose-winecard-desc {
  margin: 0;
  font-family: var(--ws-card-desc-font);
  font-size: 15px;
  font-weight: 400;
  line-height: 120%;
  color: var(--ws-text);
}

:host(.ws-tenant-goosecross) .ws-goose-winecard-meta {
  font-size: 14px;
  font-weight: 600;
  color: #4d4d4d;
}

/* Horizontal bottom row: price block left, BUY NOW right. */
:host(.ws-tenant-goosecross) .ws-goose-bottom {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  margin-top: 4px;
}
:host(.ws-tenant-goosecross) .ws-goose-priceblock {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}
:host(.ws-tenant-goosecross) .ws-goose-priceblock .ws-card-price {
  margin: 6px 0 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--ws-card-secondary-accent);
  line-height: 1.1;
}
:host(.ws-tenant-goosecross) .ws-goose-priceblock .ws-card-unit {
  font-size: 12px;
  font-weight: 600;
  color: #000;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-goose-cta {
  flex: 0 0 115px;
  width: 115px;
  box-sizing: border-box;
  display: block;
  text-align: center;
  padding: 9px;
  background: #f8d47c;
  border: 1px solid #f8d47c;
  color: #000;
  border-radius: 1px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: normal;
  white-space: nowrap;
}
:host(.ws-tenant-goosecross) .ws-goose-cta:hover {
  background: #f8d47c;
}

/* Membership card — replicate the LIVE goose `.card-box`: WHITE card with a gold
   glow, gold-brown title, description bullets, a white rounded "Learn more" pill,
   and a footer with price/unit + a gold uppercase CTA. Values pulled from old
   goose index.css (.card-box / .innercardbox / .learn-more / .innerfooter). */
:host(.ws-tenant-goosecross) .ws-rich-membership {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  overflow: hidden;
}
:host(.ws-tenant-goosecross) .ws-rich-membership .ws-card-head {
  padding: 6px;
  background: transparent;
}
:host(.ws-tenant-goosecross) .ws-rich-membership .ws-card-img {
  aspect-ratio: 16 / 9;
  border-radius: 6px;
  object-fit: cover;
}
/* No-cover fallback: padded goose logo on white (live used defaultCardLogo +
   padding:19px when cover_image_link was empty). */
:host(.ws-tenant-goosecross) .ws-card-img.ws-rich-mem-logo {
  object-fit: contain;
  background: #ffffff;
}
:host(.ws-tenant-goosecross) .ws-rich-membership .ws-card-title {
  color: var(--ws-card-secondary-accent);
  font-size: 16px;
  font-weight: 400;
  line-height: 16px;
  text-align: left;
  padding: 8px;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-rich-inner {
  background: transparent;
  padding: 8px;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}
:host(.ws-tenant-goosecross) .ws-rich-membership .ws-card-price {
  font-size: 14px;
  font-weight: 400;
  line-height: 14px;
  margin-left: 5px;
}
:host(.ws-tenant-goosecross) .ws-rich-bullets {
  list-style: none;
  margin: 6px 0 8px;
  padding: 0;
}
:host(.ws-tenant-goosecross) .ws-rich-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 4px;
  color: var(--ws-text);
}
:host(.ws-tenant-goosecross) .ws-rich-check {
  color: var(--ws-card-secondary-accent);
  flex-shrink: 0;
  margin-top: 2px;
  display: inline-flex;
}

/* Membership typography and checks from the original Goose card-box-para. */
:host(.ws-tenant-goosecross) .ws-rich-membership .ws-rich-bullets li {
  font-size: 15px;
  font-weight: 600;
  line-height: 16px;
  color: #4d4d4d;
  gap: 0;
}

:host(.ws-tenant-goosecross) .ws-rich-membership .ws-rich-bullets li > span {
  padding: 5px;
}

:host(.ws-tenant-goosecross) .ws-rich-membership .ws-rich-check {
  color: #4d4d4d;
}

:host(.ws-tenant-goosecross) .ws-rich-membership .ws-rich-check svg {
  width: 12px;
  height: 12px;
}

:host(.ws-tenant-goosecross) .ws-rich-membership .ws-card-learnmore {
  font-weight: 400;
}
:host(.ws-tenant-goosecross) .ws-card-learnmore {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 8px 12px;
  margin: auto;
  margin-bottom: 10px;
  border: 1px solid #f8d47c;
  border-radius: 20px;
  background: #ffffff;
  box-shadow: 0 4px 9.7px 0 rgba(110, 24, 17, 0.25);
  color: var(--ws-card-secondary-accent);
  font-size: 15px;
}
:host(.ws-tenant-goosecross) .ws-rich-membership-footer {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
  /* margin-top: 24px; */
}
:host(.ws-tenant-goosecross) .ws-rich-mem-price .ws-card-price-amount {
  font-size: 16px;
  color: var(--ws-card-secondary-accent);
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-rich-mem-price .ws-card-price-unit {
  font-size: 12px;
  color: #000000;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-rich-membership-footer .ws-card-cta {
  background: #f8d47c;
  color: #000000;
  border: 1px solid #f8d47c;
  border-radius: 1px;
  padding: 9px;
  font-size: 14px;
  font-weight: 600;
}

/* Event card — plain WHITE card (gold glow), matching the live `.event_card`:
   cover image on top, a header block (title/date/time/location/sub_title), then a
   footer with a "Learn more" pill and a price + gold uppercase CTA. (Was a blurred
   translucent overlay — the original goose author's anomaly; every other goose
   card is a white card with the same gold glow.) */
:host(.ws-tenant-goosecross) .ws-rich-event {
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 0 10px 0 rgba(189, 133, 39, 0.4);
  padding: 8px;
  overflow: hidden;
  /* Reset the common cover/blur event styling (cards.css) — goose's is a flat
     white card, not a cover-image card with a blurred bottom overlay. */
  min-height: 0;
  color: inherit;
  background-size: auto;
}
:host(.ws-tenant-goosecross) .ws-rich-event-inner {
  position: static;
  inset: auto;
  padding: 0;
  background: none;
  -webkit-backdrop-filter: none;
  backdrop-filter: none;
}
:host(.ws-tenant-goosecross) .ws-rich-event-details {
  overflow: visible; /* decorative top is pinned outside via negative margin */
}
:host(.ws-tenant-goosecross) .ws-rich-event .ws-card-head {
  padding: 0;
  cursor: pointer;
}
:host(.ws-tenant-goosecross) .ws-rich-event .ws-card-img {
  width: 100%;
  border-radius: 6px;
  display: block;
}
:host(.ws-tenant-goosecross) .ws-rich-event-inner {
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex: 1;
}
:host(.ws-tenant-goosecross) .ws-goose-event-header {
  text-align: left;
}
:host(.ws-tenant-goosecross) .ws-event-title {
  font-size: 16px;
  font-weight: 600;
  line-height: normal;
  color: var(--ws-card-secondary-accent);
  margin: 0 0 8px;
}
:host(.ws-tenant-goosecross) .ws-event-date {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: #000000;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-event-time {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #574d39;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-event-location {
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #574d39;
  margin: 0 0 8px;
}
:host(.ws-tenant-goosecross) .ws-event-desc {
  font-size: 12px;
  color: #574d39;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-event-footer {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-top: 24px;
}
:host(.ws-tenant-goosecross) .ws-event-footer-row {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
}
:host(.ws-tenant-goosecross) .ws-event-price-block .ws-card-price-amount {
  font-size: 16px;
  font-weight: 600;
  color: var(--ws-card-secondary-accent);
  margin: 0;
  text-align: left;
}
:host(.ws-tenant-goosecross) .ws-event-price-block .ws-card-price-unit {
  font-size: 12px;
  font-weight: 600;
  color: #000000;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-event-footer .ws-card-cta {
  background: #f8d47c;
  color: #000000;
  border: 1px solid #f8d47c;
  border-radius: 1px;
  padding: 9px;
  text-transform: none;
  font-size: 14px;
  font-weight: 600;
}

/* Event details — replicate the live .detail-event-card: light-grey card with a
   decorative eventdetail.png pinned top-right, a white rounded date pill, a bold
   heading, divider, the first description paragraph, a price row, and a brown
   reserve button. (Was a burgundy strip + cover image — original goose error.) */
:host(.ws-tenant-goosecross) .ws-rich-event-details {
  position: relative;
  background: #f6f6f6;
  max-width: 284px;
  border-radius: 8px;
  padding: 25px 10px 10px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
}
:host(.ws-tenant-goosecross) .ws-goose-detail-top {
  position: absolute;
  top: 0;
  right: 10px;
  display: flex;
  justify-content: flex-end;
  max-width: 200px;
  margin-top: -25px;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-top-img {
  max-width: 200px;
  width: 100%;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-date {
  display: inline-block;
  position: relative;
  border-radius: 48px;
  padding: 4px 10px;
  background: #ffffff;
  border: 1px solid rgba(192, 126, 50, 0.19);
  font-size: 13px;
  color: #574d39;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-heading {
  position: relative;
  font-weight: 700;
  font-size: 16px;
  line-height: 100%;
  color: #574d39;
  width: 75%;
  margin: 20px 0 8px;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-divider {
  width: 149px;
  height: 1px;
  background: rgba(0, 0, 0, 0.12);
  margin: 8px 0;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-desc {
  position: relative;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #574d39;
  width: 85%;
  margin: 0;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-price {
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 20px 0;
  color: var(--ws-card-secondary-accent);
}
:host(.ws-tenant-goosecross) .ws-goose-detail-price-detail {
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  color: #574d39;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-amount {
  font-weight: 500;
}
:host(.ws-tenant-goosecross) .ws-goose-detail-reserve {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 4px 12px 4px 6px;
  border-radius: 6px;
  background: #ad703e;
  color: #ffffff;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  box-shadow: 0 2px 2px 0 rgba(162, 155, 141, 0.15);
  margin-bottom: 10px;
}

/* PromoPoster — 12-day rotation image is full-bleed; fallback panel is a
   small centered card with text + CTA. */
:host(.ws-tenant-goosecross) .ws-goose-promo .ws-promo-poster-img {
  width: 100%;
  border-radius: 8px;
  display: block;
}
:host(.ws-tenant-goosecross) .ws-goose-promo-fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 12px;
  background: #1f1c1b;
  color: #ffffff;
  border-radius: 8px;
}
:host(.ws-tenant-goosecross) .ws-goose-promo-text {
  margin: 0;
  font-weight: 600;
  text-align: center;
}
:host(.ws-tenant-goosecross) .ws-goose-promo-cta {
  background: #e8bf5c;
  color: #1a1a1a;
}

/* Original Goose text-a-live-person form colors. */
:host(.ws-tenant-goosecross) .ws-liveperson-form .ws-checkout-submit {
  background: #a66836;
  color: #fff;
  border: none;
  border-radius: 2px;
  padding: 10px 12px;
  font-size: 15px;
  font-weight: 400;
}

:host(.ws-tenant-goosecross) .ws-liveperson-form .ws-terms-toggle {
  color: #222;
}

:host(.ws-tenant-goosecross) .ws-liveperson-form .ws-liveperson-terms {
  margin-bottom: 0;
}

/* Empty terms validation must not reserve a row between Read more and Send. */
:host(.ws-tenant-goosecross) .ws-liveperson-form > .ws-checkout-error:empty {
  display: none;
}

:host(.ws-tenant-goosecross) .ws-liveperson-form .ws-liveperson-footer {
  margin-top: 0;
}

/* Reviews button row: common look + layout for every tenant now lives in the
   shared cards.css (white pill, brown border/text, rounded, vertical stack,
   sized to content) — Goosecross no longer needs its own override. */

/* Wine Pagination */

:host(.ws-tenant-goosecross) .ws-wine-page-button {
  color: var(--ws-card-secondary-accent);
}

:host(.ws-tenant-goosecross) .ws-wine-page-button.active {
  background: var(--ws-card-secondary-accent, #8b6b33);
  border-color: var(--ws-pagination-active-bg, var(--ws-primary));
  color: var(--ws-pagination-active-color, #fff);
}

/* Tasting Notes Drawer */

:host(.ws-tenant-goosecross) .ws-drawer-tab-collapse svg path,
:host(.ws-tenant-goosecross) .ws-wine-drawer-header-icon svg path {
  stroke: var(--ws-card-secondary-accent);
}

:host(.ws-tenant-goosecross) .ws-wine-drawer-header h2,
:host(.ws-tenant-goosecross) .ws-wine-drawer-pane h4,
:host(.ws-tenant-goosecross) .ws-wine-drawer-regular-price,
:host(.ws-tenant-goosecross) .ws-drawer-fp-heading,
:host(.ws-tenant-goosecross) .ws-drawer-ingredient-bullet,
:host(.ws-tenant-goosecross) .ws-drawer-ingredient-amount {
  color: var(--ws-card-secondary-accent);
}

:host(.ws-tenant-goosecross) .ws-wine-drawer-intro,
:host(.ws-tenant-goosecross) .ws-drawer-pairing-box {
  background: var(--ws-drawer-surface);
}

:host(.ws-tenant-goosecross) .ws-wine-drawer-tab.active {
  background: var(--ws-card-accent);
  color: #000;
  border-right: 5px solid var(--ws-card-secondary-accent);
}

:host(.ws-tenant-goosecross) .ws-drawer-fp-card {
  border-top: 3px solid var(--ws-card-secondary-accent);
  background: var(--ws-drawer-surface);
}
:host(.ws-tenant-goosecross) .ws-drawer-method-num {
  background: var(--ws-card-secondary-accent);
}

/* ──────────────────────────────────────────────────────────────
   Host-page search bar (features/optional/search-bar.js).

   These rules land in the SEARCH BAR's own shadow root, not the chat panel's —
   both hosts get the `ws-tenant-goosecross` class and both are fed
   styles/tenant-css.js, so one file themes both. Structure + the --ws-search-*
   defaults live in styles/SearchBar.css; only Goosecross's placement and brand
   colours belong here.

   Placement replicates the live site's `.search-container` (old index.css
   247-260 + the 1024/1280 breakpoints): floated over the Webflow nav row,
   centred just right of middle, on the nav's centre line. The old rule left
   `top` unset and leaned on the element's static position inside the flex row,
   which lands differently depending on how the nav is laid out — `top: 50%`
   with the same translate centres it explicitly instead.
   ────────────────────────────────────────────────────────────── */
/* ⚠ `.ws-search-host` is NOT optional. tenant-css.js is injected into the chat
   panel's shadow root AND the search bar's, so a bare
   `:host(.ws-tenant-goosecross)` block here would also match
   #redconcierge-widget — these positional rules would then move the whole
   widget container (and the launcher with it) into the middle of the page. */
:host(.ws-tenant-goosecross.ws-search-host) {
  position: absolute;
  top: 50%;
  left: 55%;
  transform: translate(-50%, -50%);
  z-index: 99;
  --ws-search-width: 600px;
  /* Goose gold send arrow (old .send-btn svg fill #d5a644). */
  --ws-search-send: #d5a644;
}

/* Between the nav's desktop breakpoint and 1280px the nav row is tighter, so
   the live site narrows the bar and nudges it right. */
@media (max-width: 1279px) {
  :host(.ws-tenant-goosecross.ws-search-host) {
    left: 58%;
    --ws-search-width: 370px;
  }
}
